import classNames from 'classnames'
import type { FC } from 'react'

import { Button } from 'common/components/Button/Button'

import type { DayResults, SlotOption } from './ehrAppointmentV2.types'
import styles from './ehrAppointmentV2.module.scss'
import { formatDuration, formatMinutes, slotKey } from './pairing'

type Props = {
  days: DayResults[]
  isFetching: boolean
  hasError: boolean
  isUnreadable: boolean
  isDisabled?: boolean
  selectedKey: string | null
  hint?: string
  onSelect: (slot: SlotOption) => void
  onRetry: () => void
}

export const ResultsList: FC<Props> = ({
  days,
  isFetching,
  hasError,
  isUnreadable,
  isDisabled,
  selectedKey,
  hint = 'Your EMR availability. Rooms are not checked yet.',
  onSelect,
  onRetry,
}) => {
  const total = days.reduce((count, day) => count + day.total, 0)

  return (
    <div className={styles.resultsList}>
      <div className={styles.resultsHeader}>
        <span className={styles.resultsCount}>{isFetching ? '—' : String(total)}</span>
        <span className={styles.resultsLabel}>open slots</span>
        <span className={styles.fieldGrow} />
        <span className={styles.resultsNote}>earliest first</span>
      </div>

      {/* OpenEMR searches the signed-in doctor's calendar; a chosen room narrows it on this side. */}
      <p className={styles.resultsHint}>{hint}</p>

      <div className={styles.resultsScroll}>
        {isFetching && <p className={styles.resultsLoading}>Looking for open slots…</p>}

        {!isFetching && (hasError || isUnreadable) && (
          <div className={styles.resultsEmpty}>
            <span className={styles.resultsEmptyTitle}>Could not read availability</span>
            <span className={styles.resultsEmptyBody}>
              {hasError
                ? 'The schedule did not answer. Your entries are untouched.'
                : 'The schedule answered with times this dialog could not read. Pick a time by hand, or retry.'}
            </span>
            <Button onClick={onRetry}>Retry</Button>
          </div>
        )}

        {!isFetching &&
          !hasError &&
          !isUnreadable &&
          days.map((day) => (
            <div key={day.day}>
              <div className={styles.dayHeader}>
                <span>{day.label}</span>
                <span className={styles.fieldGrow} />
                <span className={styles.resultsNote}>
                  {day.total} {day.total === 1 ? 'option' : 'options'}
                </span>
              </div>

              {day.options.map((slot) => {
                const key = slotKey(slot)
                const isActive = key === selectedKey

                return (
                  <button
                    type='button'
                    key={key}
                    aria-pressed={isActive}
                    disabled={isDisabled}
                    className={classNames(styles.optionRow, {
                      [styles.optionRowActive]: isActive,
                    })}
                    onClick={() => onSelect(slot)}>
                    <span className={styles.optionLeg}>
                      <span className={styles.legTime}>{formatMinutes(slot.start)}</span>
                      <span className={styles.legLabel}>
                        {formatDuration(slot.end - slot.start)}
                      </span>
                    </span>
                    <span className={styles.optionMeta}>
                      <span className={classNames(styles.gapBadge, styles.gapBadgeLoose)}>
                        ENDS {formatMinutes(slot.end)}
                      </span>
                    </span>
                  </button>
                )
              })}
            </div>
          ))}

        {!isFetching && !hasError && !isUnreadable && days.length === 0 && (
          <div className={styles.resultsEmpty}>
            <span className={styles.resultsEmptyTitle}>No open slots in this window</span>
            <span className={styles.resultsEmptyBody}>
              Extend the window, change the duration, or pick a time by hand.
            </span>
          </div>
        )}
      </div>
    </div>
  )
}
